import React, { useState, useEffect } from 'react';
import VersionBadge from '../VersionBadge/VersionBadge';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Badge,
  Spinner,
  Input,
  Label,
} from 'reactstrap';
import moment from 'moment';
import { useOrganizationStore } from '../../store/organization.store';
import { collection, getDocs, addDoc, query, orderBy } from 'firebase/firestore';
import { getOrgCollectionPath } from '../../constants/paths';
import { getAuth } from 'firebase/auth';

// Fallback QC profiles (used if Firestore is empty)
const DEFAULT_QC_PROFILES: Record<string, { name: string; description: string }> = {
  ebu_r128: { name: 'EBU R128 (Europe)', description: 'Target: -23 LUFS, Peak: -2 dBTP' },
  atsc_a85: { name: 'ATSC A/85 (North America)', description: 'Target: -24 LUFS, Peak: -2 dBTP' },
  sabc: { name: 'SABC (South Africa)', description: 'Target: -23 LUFS, Peak: -3 dBTP' },
  relaxed: { name: 'Relaxed (Demo/Review)', description: 'Target: -23 LUFS ±3, Peak: -1 dBTP' },
};

interface QCRecord {
  id: string;
  status: 'pass' | 'fail' | 'testing';
  profileName: string;
  profileId: string;
  fileVersion: number;
  qcVersion: number;
  qcTestNumber: number;
  submittedAt: number;
  submittedBy: string;
  submittedByName: string;
  submittedByEmail: string;
  completedAt?: number;
  summary?: string;
  checks?: any[];
  loudness?: any;
  audioFormat?: any;
  clippingEvents?: number;
  fileName?: string;
}

interface QCHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  trackName: string;
  trackId: string;
  track: any;
  firebase: any;
  onUpdate?: () => void;
}

// QC Service URL — configurable per environment
const QC_SERVICE_URL = (window as any).__QC_SERVICE_URL__ || process.env.REACT_APP_QC_SERVICE_URL || 'http://localhost:8080';

const QCHistoryModal: React.FC<QCHistoryModalProps> = ({
  isOpen,
  onClose,
  onUpdate,
  trackName,
  trackId,
  track,
  firebase,
}) => {
  const [qcRecords, setQcRecords] = useState<QCRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState('ebu_r128');
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  // Per-session dismissal for the QC-fail guidance callout. Tracked by
  // the failed record's id so a NEW fail later in the same session
  // brings the callout back — dismissal is scoped to "this specific
  // failed result", not "any future fail". State lives on the
  // component so it resets when the modal is closed + reopened.
  const [dismissedFailId, setDismissedFailId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [qcProfiles, setQcProfiles] = useState<Record<string, { name: string; description: string }>>(DEFAULT_QC_PROFILES);
  const [qcProfileData, setQcProfileData] = useState<Record<string, any>>({});
  const [orgDefaultProfile, setOrgDefaultProfile] = useState<string | null>(null);

  const { currentOrgId } = useOrganizationStore();

  // Load QC profiles from Firestore + org default
  useEffect(() => {
    const loadProfiles = async () => {
      if (!firebase) return;
      try {
        // Load global profiles
        const profilesRef = collection(firebase.db, 'qcProfiles');
        const snapshot = await getDocs(profilesRef);
        if (!snapshot.empty) {
          const loaded: Record<string, { name: string; description: string }> = {};
          const fullData: Record<string, any> = {};
          snapshot.docs.forEach(d => {
            const data = d.data();
            loaded[d.id] = {
              name: data.name || d.id,
              description: `Target: ${data.loudness_target} LUFS, Peak: ${data.max_true_peak} dBTP`,
            };
            fullData[d.id] = data;
          });
          setQcProfiles(loaded);
          setQcProfileData(fullData);

          // Load org's default profile preference
          if (currentOrgId) {
            try {
              const { doc: firestoreDoc, getDoc: firestoreGetDoc } = await import('firebase/firestore');
              const settingsRef = firestoreDoc(firebase.db, `organizations/${currentOrgId}/settings/qc`);
              const settingsDoc = await firestoreGetDoc(settingsRef);
              const defaultId = settingsDoc.exists() ? settingsDoc.data()?.defaultProfile : null;
              if (defaultId && loaded[defaultId]) {
                setSelectedProfile(defaultId);
                setOrgDefaultProfile(defaultId);
              } else if (!loaded[selectedProfile]) {
                setSelectedProfile(Object.keys(loaded)[0] || 'ebu_r128');
              }
            } catch {
              if (!loaded[selectedProfile]) {
                setSelectedProfile(Object.keys(loaded)[0] || 'ebu_r128');
              }
            }
          }
        }
      } catch {
        // Fall back to defaults
      }
    };
    if (isOpen) loadProfiles();
  }, [isOpen, firebase, currentOrgId]);

  // Load QC records when modal opens
  // For delivery tracks, read from source org's subcollection (shared history)
  useEffect(() => {
    const loadRecords = async () => {
      if (!isOpen || !currentOrgId || !firebase) return;

      setLoading(true);
      try {
        // Any received copy (delivery OR campaign) reads QC records from the
        // source org's audiometa — copies don't carry their own qcRecords
        // subcollection. The source org is the QC authority.
        const isReceivedCopy = (track?.isDeliveryTrack || (track as any)?.isCampaignCopy) && track?.sourceOrgId;
        const qcOrgId = isReceivedCopy ? track!.sourceOrgId! : currentOrgId;
        const qcRef = collection(
          firebase.db,
          getOrgCollectionPath(qcOrgId, 'audiometa'),
          trackId,
          'qcRecords'
        );
        const qcQuery = query(qcRef, orderBy('submittedAt', 'desc'));
        const snapshot = await getDocs(qcQuery);
        const records = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as QCRecord));
        setQcRecords(records);
      } catch (err) {
        console.error('Error loading QC records:', err);
      }
      setLoading(false);
    };

    loadRecords();
  }, [isOpen, currentOrgId, trackId, firebase, track?.sourceOrgId, track?.isDeliveryTrack, (track as any)?.isCampaignCopy]);

  // Set default version to latest
  useEffect(() => {
    if (isOpen && track) {
      setSelectedVersion(track.fileVersion || 1);
    }
  }, [isOpen, track]);

  // Get the WAV file for a specific version
  const getWavFileForVersion = (version: number): any | null => {
    const wavFiles = track?.wavFiles || [];
    // Find WAV with matching version
    const versionedWav = wavFiles.find((f: any) => f.version === version);
    if (versionedWav) return versionedWav;
    // Fallback: if only one WAV and no version tags, use it
    if (wavFiles.length === 1 && !wavFiles[0].version) return wavFiles[0];
    return null;
  };

  // Get available versions from WAV files
  const getAvailableVersions = (): number[] => {
    const wavFiles = track?.wavFiles || [];
    const versions = wavFiles
      .filter((f: any) => f.version)
      .map((f: any) => f.version as number)
      .sort((a: number, b: number) => b - a);
    if (versions.length === 0 && wavFiles.length > 0) return [1];
    return versions;
  };

  // Submit for QC
  const handleSubmitQC = async () => {
    if (!currentOrgId || !firebase || submitting) return;

    const version = selectedVersion || track?.fileVersion || 1;
    const wavFile = getWavFileForVersion(version);

    if (!wavFile || !wavFile.downloadUrl) {
      setError('No WAV file found for the selected version');
      return;
    }

    setSubmitting(true);
    setError(null);

    const auth = getAuth();
    const user = auth.currentUser;

    // Write QC records to source org's subcollection for shared history.
    // Applies to any received copy (delivery OR campaign) — the source
    // org's audiometa is the canonical QC history location.
    const isCrossOrg = (track?.isDeliveryTrack || (track as any)?.isCampaignCopy) && track?.sourceOrgId && track.sourceOrgId !== currentOrgId;
    const qcOrgId = isCrossOrg ? track!.sourceOrgId! : currentOrgId;
    const qcRef = collection(
      firebase.db,
      getOrgCollectionPath(qcOrgId, 'audiometa'),
      trackId,
      'qcRecords'
    );

    // Calculate QC test number: count existing tests for THIS file version + 1.
    // IMPORTANT: fetch fresh from Firestore rather than trusting the in-memory
    // `qcRecords` state. Two race conditions can leave that state empty at
    // submit time even after the display later shows the right records:
    //   1. Fast-click race — user hits Run QC before the initial load
    //      useEffect finishes its async getDocs.
    //   2. Received-copy timing — master (or any campaign-copy viewer) may
    //      briefly see a track without isCampaignCopy set, causing the load
    //      to read from the wrong org's qcRecords collection.
    // A fresh fetch at submit time uses the actual authoritative count.
    let existingTestsForVersion = 0;
    try {
      const freshQuery = query(qcRef, orderBy('submittedAt', 'desc'));
      const freshSnap = await getDocs(freshQuery);
      existingTestsForVersion = freshSnap.docs.filter((d) => {
        const data = d.data() as any;
        return data.fileVersion === version;
      }).length;
    } catch (err) {
      console.warn('QC record count fetch failed, falling back to in-memory count:', err);
      existingTestsForVersion = qcRecords.filter(r => r.fileVersion === version).length;
    }
    const qcTestNumber = existingTestsForVersion + 1;

    const currentOrgNameVal = useOrganizationStore.getState().currentOrgName || '';
    const qcData: any = {
      status: 'testing',
      profileId: selectedProfile,
      profileName: qcProfiles[selectedProfile]?.name || selectedProfile,
      fileVersion: version,
      qcTestNumber: qcTestNumber,
      qcVersion: track?.qcVersion || 0,
      fileName: wavFile.storageRef || wavFile.fileName,
      submittedAt: Date.now(),
      submittedBy: user?.uid || '',
      submittedByName: user?.displayName || user?.email || '',
      submittedByEmail: user?.email || '',
      ranByOrgId: currentOrgId,
      ranByOrgName: currentOrgNameVal,
    };

    try {
      const docRef = await addDoc(qcRef, qcData);
      const newRecord = { id: docRef.id, ...qcData } as QCRecord;
      setQcRecords(prev => [newRecord, ...prev]);

      // Update track's qc_status to 'testing'.
      //
      // Source-of-audio (qcOrgId) is the canonical write target. When the
      // acting org is a campaign-copy viewer (e.g. master runs QC on a
      // spot originally uploaded by the studio), we still write the
      // authoritative state to the source. Every campaign / delivery /
      // review copy of the same track sees the same status once we
      // cascade via syncTrackFieldsAcrossOrgs at the end.
      const { doc: firestoreDocRef, updateDoc: firestoreUpdateDoc } = await import('firebase/firestore');
      const trackRef = firestoreDocRef(firebase.db, getOrgCollectionPath(qcOrgId, 'audiometa'), trackId);
      await firestoreUpdateDoc(trackRef, { qc_status: 'testing' });
      // Also cascade the "testing" flag to copies so their badges flip
      // immediately (not just after the run completes).
      try {
        await firebase.syncTrackFieldsAcrossOrgs(qcOrgId, trackId, { qc_status: 'testing' });
      } catch { /* best effort */ }

      // Call the QC service
      const response = await fetch(`${QC_SERVICE_URL}/analyze`, {
        method: 'POST',
        body: await createFormData(wavFile.downloadUrl, wavFile.storageRef || wavFile.fileName, selectedProfile),
      });

      if (!response.ok) {
        throw new Error(`QC service returned ${response.status}`);
      }

      const report = await response.json();

      // Update the QC record with results
      const updateData = {
        status: report.passed ? 'pass' : 'fail',
        completedAt: Date.now(),
        summary: report.summary,
        checks: report.checks,
        loudness: report.loudness,
        audioFormat: report.audio_format,
        clippingEvents: report.clipping_events,
      };

      const { doc: firestoreDoc, updateDoc } = await import('firebase/firestore');
      await updateDoc(firestoreDoc(qcRef, docRef.id), updateData);

      // Update track's qc_status and broadcast master reference if passed
      const trackUpdate: any = {
        qc_status: report.passed ? 'pass' : 'fail',
        qcVersion: qcTestNumber,
      };

      // Update qcVersion and fileName on file entries for the current file version
      const updateFileEntries = (files: any[]) =>
        files.map(f => {
          if ((f.version || 1) !== version) return f;
          const updated = { ...f, qcVersion: qcTestNumber };
          // Update fileName to reflect new QC version (e.g. _qc1.mp3 → _qc2.mp3)
          if (f.fileName) {
            updated.fileName = f.fileName.replace(/_qc\d+/, `_qc${qcTestNumber}`);
          }
          return updated;
        });
      if (track?.mp3Files) trackUpdate.mp3Files = updateFileEntries(track.mp3Files);
      if (track?.wavFiles) trackUpdate.wavFiles = updateFileEntries(track.wavFiles);

      if (report.passed) {
        // Lock the broadcast master — store exact file reference
        trackUpdate.is_broadcast_master = true;
        trackUpdate.broadcast_master_file = {
          storageRef: wavFile.storageRef || wavFile.fileName,
          downloadUrl: wavFile.downloadUrl,
          fileVersion: version,
          qcVersion: qcTestNumber,
          qcRecordId: docRef.id,
          qcPassedAt: Date.now(),
          qcProfile: selectedProfile,
          qcProfileName: qcProfiles[selectedProfile]?.name || selectedProfile,
        };
      }

      // Write authoritative state to the source-of-audio track. This is
      // the doc under `/organizations/{qcOrgId}/audiometa/{trackId}` — the
      // one that owns the QC record subcollection we just wrote to.
      await updateDoc(trackRef, trackUpdate);

      // Fast-path: also write DIRECTLY to the acting org's copy so the
      // local tracks-store snapshot listener fires immediately with the
      // new qc_status / qcVersion / files. Without this the user has to
      // wait for the collection-group cascade below (multiple round-trips
      // via getDocs + sequential updateDocs), which can take a few
      // seconds and delays the badge refresh in the acting org's library.
      // Redundant with the cascade write for the same doc, but the cascade
      // uses merge semantics so the double-write is idempotent.
      if (currentOrgId && currentOrgId !== qcOrgId) {
        try {
          const localRef = firestoreDoc(firebase.db, getOrgCollectionPath(currentOrgId, 'audiometa'), trackId);
          await updateDoc(localRef, trackUpdate);
        } catch { /* best effort — will be caught by the cascade below */ }
      }

      // Cascade to every campaign / delivery / review copy of the track
      // across all orgs. Any org that has a mirror sees the new qc_status,
      // qcVersion, file entries, and (if passed) the broadcast-master
      // reference in one atomic-ish operation.
      try {
        await firebase.syncTrackFieldsAcrossOrgs(qcOrgId, trackId, trackUpdate);
      } catch { /* best effort */ }

      // Update local state
      setQcRecords(prev => prev.map(r =>
        r.id === docRef.id ? { ...r, ...updateData } as QCRecord : r
      ));

    } catch (err: any) {
      console.error('QC submission error:', err);
      setError(`QC failed: ${err.message}`);

      // Update the record to show error
      // Keep status as 'testing' failed — user can retry
    } finally {
      setSubmitting(false);
    }
  };

  // Fetch WAV from URL and create FormData for the QC service
  const createFormData = async (downloadUrl: string, fileName: string, profile: string): Promise<FormData> => {
    const response = await fetch(downloadUrl);
    const blob = await response.blob();
    const formData = new FormData();
    formData.append('file', blob, fileName);
    // Send full profile data from Firestore if available (overrides backend defaults)
    const profileFull = qcProfileData[profile];
    if (profileFull) {
      formData.append('profile_data', JSON.stringify(profileFull));
    } else {
      formData.append('profile', profile);
    }
    return formData;
  };

  const getStatusBadge = (record: QCRecord) => {
    const statusMap: Record<string, { color: string; label: string }> = {
      pass: { color: '#50c878', label: 'Pass' },
      fail: { color: 'var(--button-danger)', label: 'Fail' },
      testing: { color: 'var(--button-warning)', label: 'Testing' },
    };
    const s = statusMap[record.status] || { color: 'rgba(255,255,255,0.5)', label: record.status };
    return (
      <span className="version-badge" style={{
        padding: '1px 5px', borderRadius: '3px', fontSize: '0.6rem', fontWeight: 600,
        backgroundColor: `color-mix(in srgb, ${s.color} 20%, transparent)`,
        whiteSpace: 'nowrap',
        ['--badge-color' as any]: s.color,
      } as React.CSSProperties}>
        {s.label}
      </span>
    );
  };

  const getCheckIcon = (passed: boolean) => (
    <span style={{ color: passed ? '#50c878' : '#ff5050', marginRight: '0.5rem' }}>
      {passed ? '✓' : '✗'}
    </span>
  );

  const availableVersions = getAvailableVersions();

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      style={{
        maxWidth: '700px',
        margin: '1.75rem auto',
      }}
      contentClassName="delete-confirm-modal"
    >
      <ModalHeader
        toggle={onClose}
        style={{
          backgroundColor: 'var(--chatbot-color-2)',
          color: '#ffffff',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        }}
        close={
          <button className="close" onClick={onClose} style={{
            color: 'var(--accent-1-color)', opacity: 1, textShadow: 'none',
            background: 'none', border: 'none', fontSize: '1.75rem', fontWeight: 'normal', cursor: 'pointer',
          }}>
            &times;
          </button>
        }
      >
        <span style={{ color: '#fff' }}>QC History - {trackName}</span>
      </ModalHeader>

      <ModalBody style={{
        backgroundColor: 'var(--chatbot-color-1)',
        color: '#ffffff',
        padding: '1.5rem',
        maxHeight: '500px',
        overflowY: 'auto',
      }}>
        {/* QC-failed guidance callout — appears immediately after QC
            returns a fail so the operator knows what to do next
            (before, they'd land on the history list and might not
            notice the small "Update" button next to the failed row).
            Dismissible per-session; re-appears if a NEW fail lands
            after dismissal. */}
        {(() => {
          const latestFail = qcRecords.find((r, i) =>
            r.status === 'fail' && !qcRecords.slice(0, i).some((x) => x.status === 'fail'),
          );
          if (!latestFail) return null;
          if (dismissedFailId === latestFail.id) return null;
          return (
            <div style={{
              marginBottom: '1.25rem',
              padding: '1rem 1.1rem',
              backgroundColor: 'rgba(255, 80, 80, 0.08)',
              border: '1px solid rgba(255, 80, 80, 0.3)',
              borderRadius: '8px',
              position: 'relative',
            }}>
              <button
                type="button"
                onClick={() => setDismissedFailId(latestFail.id)}
                title="Dismiss (comes back if a new QC fails)"
                style={{
                  position: 'absolute', top: '0.4rem', right: '0.6rem',
                  background: 'none', border: 'none',
                  color: 'rgba(255, 255, 255, 0.4)',
                  fontSize: '1.1rem', lineHeight: 1, cursor: 'pointer',
                  padding: '2px 6px',
                }}
              >
                &times;
              </button>
              <div style={{
                color: '#ff5050', fontWeight: 600, fontSize: '0.95rem',
                marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.4rem',
              }}>
                <span style={{ fontSize: '1rem' }}>❌</span>
                QC Failed
              </div>
              <div style={{
                color: 'rgba(255, 255, 255, 0.85)', fontSize: '0.85rem', lineHeight: 1.55,
                marginBottom: '0.6rem',
              }}>
                The file has issues that prevent it from being broadcast. To fix:
              </div>
              <ol style={{
                paddingLeft: '1.35rem', margin: 0,
                color: 'rgba(255, 255, 255, 0.85)', fontSize: '0.85rem', lineHeight: 1.7,
              }}>
                <li>Prepare a corrected version of the file locally</li>
                <li>
                  Click the <strong style={{ color: '#ffb432' }}>Update</strong> button below (next to the failed QC record) — the track edit page opens
                </li>
                <li>Upload the corrected file and save - Do not delete the failed file</li>
                <li>Open this QC modal again and submit the new version for QC</li>
              </ol>
              <div style={{
                marginTop: '0.75rem', paddingTop: '0.6rem',
                borderTop: '1px solid rgba(255, 255, 255, 0.08)',
                color: 'rgba(255, 255, 255, 0.55)', fontSize: '0.78rem', lineHeight: 1.5,
              }}>
                Uploading a new file version preserves this QC history AND keeps every campaign link intact. Deleting the track instead would break every campaign referencing it.
              </div>
            </div>
          );
        })()}
        {/* Submit for QC section */}
        <div style={{
          marginBottom: '1.5rem',
          padding: '1rem',
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '8px',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        }}>
          <Label style={{ color: '#fff', fontWeight: '500', marginBottom: '0.75rem', display: 'block' }}>
            Submit for QC
          </Label>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.75rem', alignItems: 'flex-end' }}>
            {/* Profile selector */}
            <div style={{ flex: 1, minWidth: '200px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '4px' }}>
                <Label style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.85rem', margin: 0 }}>QC Profile</Label>
                {selectedProfile === orgDefaultProfile ? (
                  <span style={{
                    fontSize: '0.6rem', padding: '1px 5px', borderRadius: '3px',
                    backgroundColor: 'rgba(80, 200, 120, 0.2)', color: '#50c878',
                  }}>
                    Default
                  </span>
                ) : (
                  <button
                    onClick={async () => {
                      if (!firebase || !currentOrgId) return;
                      try {
                        const { doc: firestoreDoc, setDoc: firestoreSetDoc } = await import('firebase/firestore');
                        const settingsRef = firestoreDoc(firebase.db, `organizations/${currentOrgId}/settings/qc`);
                        await firestoreSetDoc(settingsRef, { defaultProfile: selectedProfile }, { merge: true });
                        setOrgDefaultProfile(selectedProfile);
                      } catch (err) {
                        console.error('Error setting default profile:', err);
                      }
                    }}
                    title="Set as default for this organization"
                    style={{
                      background: 'none', border: '1px solid rgba(255,255,255,0.15)',
                      color: 'rgba(255,255,255,0.4)', cursor: 'pointer',
                      fontSize: '0.6rem', padding: '1px 5px', borderRadius: '3px',
                    }}
                  >
                    Set Default
                  </button>
                )}
              </div>
              <Input
                type="select"
                value={selectedProfile}
                onChange={(e) => setSelectedProfile(e.target.value)}
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  color: '#fff',
                  borderRadius: '4px',
                }}
              >
                {Object.entries(qcProfiles).map(([id, profile]) => (
                  <option key={id} value={id} style={{ backgroundColor: '#1e1e2f', color: '#fff' }}>
                    {profile.name}{id === orgDefaultProfile ? ' (default)' : ''}
                  </option>
                ))}
              </Input>
            </div>
            {/* Version selector */}
            <div style={{ minWidth: '100px' }}>
              <Label style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.85rem' }}>Version</Label>
              <Input
                type="select"
                value={selectedVersion || ''}
                onChange={(e) => setSelectedVersion(Number(e.target.value))}
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  color: '#fff',
                  borderRadius: '4px',
                }}
              >
                {availableVersions.map(v => (
                  <option key={v} value={v} style={{ backgroundColor: '#1e1e2f', color: '#fff' }}>
                    v{v}
                  </option>
                ))}
              </Input>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <Button
              className="btn-primary-custom"
              onClick={handleSubmitQC}
              disabled={submitting || availableVersions.length === 0}
              size="sm"
            >
              <span style={{ position: 'relative', zIndex: 2 }}>
                {submitting ? (
                  <>
                    <Spinner size="sm" className="mr-2" /> Analyzing...
                  </>
                ) : 'Run QC'}
              </span>
            </Button>
            {availableVersions.length === 0 && (
              <span style={{ color: 'rgba(255, 180, 50, 0.8)', fontSize: '0.85rem' }}>
                No WAV files available
              </span>
            )}
          </div>
          {error && (
            <div style={{
              marginTop: '0.75rem', padding: '0.5rem 0.75rem',
              backgroundColor: 'rgba(255, 80, 80, 0.1)', borderRadius: '4px',
              color: '#ff5050', fontSize: '0.85rem',
            }}>
              {error}
            </div>
          )}
        </div>

        {/* QC History */}
        {loading ? (
          <div className="text-center py-4">
            <Spinner style={{ color: 'var(--accent-1-color)' }} size="sm" />
          </div>
        ) : qcRecords.length === 0 ? (
          <div className="text-center py-4">
            <p className="text-muted mb-0">No QC tests have been run on this spot yet.</p>
          </div>
        ) : (
          qcRecords.map((record, recordIdx) => {
            const isExpanded = expandedId === record.id;
            const isLatestFailed = record.status === 'fail' && !qcRecords.slice(0, recordIdx).some(r => r.status === 'fail');
            // Determine approval status for this record's file version
            const currentFileVer = track?.fileVersion || 1;
            const recordApprovalStatus = record.fileVersion < currentFileVer ? 'rejected' : track?.approval_status;
            // Check if the file this QC was run on still exists (may have been replaced)
            const currentWavRefs = (track?.wavFiles || []).map((f: any) => f.storageRef);
            const fileStillExists = !record.fileName || currentWavRefs.includes(record.fileName);
            // If file was replaced, mark as superseded regardless of QC result
            const effectiveQcStatus = fileStillExists ? record.status : 'fail';
            return (
              <div
                key={record.id}
                style={{
                  marginBottom: '0.75rem',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '8px',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  overflow: 'hidden',
                }}
              >
                {/* Summary row */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '0.75rem 1rem',
                    cursor: record.status !== 'testing' ? 'pointer' : 'default',
                    transition: 'background-color 0.15s',
                  }}
                  onClick={() => record.status !== 'testing' && setExpandedId(isExpanded ? null : record.id)}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                    {record.status !== 'testing' && (
                      <span style={{ color: 'rgba(255, 255, 255, 0.4)', fontSize: '0.75rem', transition: 'transform 0.2s', transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}>
                        &#9654;
                      </span>
                    )}
                    {record.status === 'testing' && (
                      <Spinner size="sm" style={{ color: '#ffb432', width: '12px', height: '12px' }} />
                    )}
                    {getStatusBadge(record)}
                    <VersionBadge type="version" version={record.fileVersion} status={recordApprovalStatus} />
                    {record.qcTestNumber && (
                      <VersionBadge type="qc" version={record.qcTestNumber} status={effectiveQcStatus} />
                    )}
                    <span style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.8rem' }}>
                      {moment(record.submittedAt).format('DD/MM/YYYY HH:mm')}
                    </span>
                    <span style={{ color: 'rgba(255, 255, 255, 0.3)', fontSize: '0.75rem' }}>
                      — {record.profileName}
                    </span>
                    {/* Update button for the most recent failed QC only */}
                    {isLatestFailed && onUpdate && (
                      <button
                        onClick={(e) => { e.stopPropagation(); onUpdate(); }}
                        style={{
                          marginLeft: 'auto',
                          padding: '2px 8px', fontSize: '0.7rem', borderRadius: '4px',
                          background: 'none', border: '1px solid rgba(255,180,50,0.3)',
                          color: '#ffb432', cursor: 'pointer',
                        }}
                      >
                        Update
                      </button>
                    )}
                  </div>
                </div>

                {/* Expanded detail */}
                {isExpanded && record.status !== 'testing' && (
                  <div style={{
                    padding: '0 1rem 1rem 1rem',
                    borderTop: '1px solid rgba(255, 255, 255, 0.08)',
                  }}>
                    {/* Submission details */}
                    <div style={{
                      marginTop: '0.75rem', padding: '0.75rem',
                      backgroundColor: 'rgba(255, 255, 255, 0.03)', borderRadius: '6px',
                      marginBottom: '0.5rem',
                    }}>
                      <div style={{ fontSize: '0.75rem', fontWeight: '600', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Submission
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.375rem', fontSize: '0.85rem' }}>
                        <div>
                          <span style={{ color: 'rgba(255, 255, 255, 0.4)' }}>By: </span>
                          <span style={{ color: '#fff' }}>{record.submittedByName || record.submittedByEmail || '--'}</span>
                        </div>
                        <div>
                          <span style={{ color: 'rgba(255, 255, 255, 0.4)' }}>Date: </span>
                          <span style={{ color: '#fff' }}>{moment(record.submittedAt).format('DD/MM/YYYY HH:mm')}</span>
                        </div>
                        {(record as any).ranByOrgName && (
                          <div>
                            <span style={{ color: 'rgba(255, 255, 255, 0.4)' }}>Org: </span>
                            <span style={{ color: '#fff' }}>{(record as any).ranByOrgName}</span>
                          </div>
                        )}
                        <div>
                          <span style={{ color: 'rgba(255, 255, 255, 0.4)' }}>Profile: </span>
                          <span style={{ color: '#fff' }}>{record.profileName}</span>
                        </div>
                        <div>
                          <span style={{ color: 'rgba(255, 255, 255, 0.4)' }}>Version: </span>
                          <span style={{ display: 'inline-flex', gap: '4px', alignItems: 'center' }}>
                            <VersionBadge type="version" version={record.fileVersion} status={recordApprovalStatus} />
                            {record.qcTestNumber && <VersionBadge type="qc" version={record.qcTestNumber} status={effectiveQcStatus} />}
                          </span>
                        </div>
                        {record.qcTestNumber && (
                          <div>
                            <span style={{ color: 'rgba(255, 255, 255, 0.4)' }}>QC Test: </span>
                            <span style={{ color: '#fff' }}>#{record.qcTestNumber}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Check results */}
                    {record.checks && record.checks.length > 0 && (
                      <div style={{
                        padding: '0.75rem',
                        backgroundColor: record.status === 'pass' ? 'rgba(80, 200, 120, 0.05)' : 'rgba(255, 80, 80, 0.05)',
                        borderRadius: '6px',
                        border: `1px solid ${record.status === 'pass' ? 'rgba(80, 200, 120, 0.15)' : 'rgba(255, 80, 80, 0.15)'}`,
                        marginBottom: '0.5rem',
                      }}>
                        <div style={{ fontSize: '0.75rem', fontWeight: '600', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Results — {record.summary}
                        </div>
                        {record.checks.map((check: any, idx: number) => (
                          <div key={idx} style={{
                            display: 'flex', alignItems: 'center', gap: '0.5rem',
                            padding: '0.25rem 0', fontSize: '0.85rem',
                            borderBottom: idx < record.checks!.length - 1 ? '1px solid rgba(255, 255, 255, 0.05)' : 'none',
                          }}>
                            {getCheckIcon(check.passed)}
                            <span style={{ color: '#fff', minWidth: '140px' }}>{check.name}</span>
                            <span style={{ color: 'rgba(255, 255, 255, 0.5)', flex: 1 }}>
                              {check.actual}
                            </span>
                            <span style={{ color: 'rgba(255, 255, 255, 0.3)', fontSize: '0.75rem' }}>
                              (expected: {check.expected})
                            </span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Loudness details */}
                    {record.loudness && (
                      <div style={{
                        padding: '0.75rem',
                        backgroundColor: 'rgba(255, 255, 255, 0.03)', borderRadius: '6px',
                      }}>
                        <div style={{ fontSize: '0.75rem', fontWeight: '600', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Loudness Measurements
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.375rem', fontSize: '0.85rem' }}>
                          <div>
                            <span style={{ color: 'rgba(255, 255, 255, 0.4)' }}>Integrated: </span>
                            <span style={{ color: '#fff' }}>{record.loudness.integrated_loudness} LUFS</span>
                          </div>
                          <div>
                            <span style={{ color: 'rgba(255, 255, 255, 0.4)' }}>True Peak: </span>
                            <span style={{ color: '#fff' }}>{record.loudness.true_peak} dBTP</span>
                          </div>
                          <div>
                            <span style={{ color: 'rgba(255, 255, 255, 0.4)' }}>LRA: </span>
                            <span style={{ color: '#fff' }}>{record.loudness.loudness_range} LU</span>
                          </div>
                          <div>
                            <span style={{ color: 'rgba(255, 255, 255, 0.4)' }}>Max Momentary: </span>
                            <span style={{ color: '#fff' }}>{record.loudness.max_momentary} LUFS</span>
                          </div>
                          <div>
                            <span style={{ color: 'rgba(255, 255, 255, 0.4)' }}>Max Short-term: </span>
                            <span style={{ color: '#fff' }}>{record.loudness.max_short_term} LUFS</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </ModalBody>

      <ModalFooter style={{
        backgroundColor: 'var(--chatbot-color-2)',
        borderTop: '1px solid rgba(255, 255, 255, 0.1)',
        justifyContent: 'flex-end',
        padding: '1rem 1.5rem',
      }}>
        <Button className="btn-info-custom" onClick={onClose}>
          <span style={{ position: 'relative', zIndex: 2 }}>Close</span>
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default QCHistoryModal;
